import React, { useState } from 'react';
import { Container, Typography, Card, CardContent, Box, Button, Stack, Chip, CircularProgress, LinearProgress } from '@mui/material';
import { api, geminiFetchSentimentBatch } from '../api'; // Import the new batch function
import { MentionCard } from '../components/MentionCard';

const SentimentPage = ({ state, refreshData, onGenerateReply }) => {
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [filter, setFilter] = useState('All');
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    const handleAnalyze = async () => {
        const unanalyzed = state.mentions.filter(m => m.id && (!m.sentiment || m.sentiment === 'Unanalyzed'));
        if (unanalyzed.length === 0) return alert('No unanalyzed mentions to process.');
        
        setIsAnalyzing(true);
        setProgress({ current: 0, total: unanalyzed.length });

        const BATCH_SIZE = 10;
        const allAnalyzedMentions = [];

        for (let i = 0; i < unanalyzed.length; i += BATCH_SIZE) {
            const batch = unanalyzed.slice(i, i + BATCH_SIZE);
            try {
                // Call the new batch function for each chunk
                const results = await geminiFetchSentimentBatch(batch);
                allAnalyzedMentions.push(...results);
            } catch (error) {
                console.error(`Failed to analyze batch starting at index ${i}`, error);
                allAnalyzedMentions.push(...batch.map(m => ({ id: m.id, sentiment: 'Unanalyzed' })));
            }
            // Update progress after each batch is processed
            setProgress(prev => ({ ...prev, current: i + batch.length }));
        }

        try {
            // Send all results to the backend in a single request
            if (allAnalyzedMentions.length > 0) {
                await api.put(`/mentions/batch-sentiment`, allAnalyzedMentions);
            }
            alert('Analysis complete! Refreshing data...');
        } catch (error) {
            alert('Failed to save some sentiment updates to the database.');
        } finally {
            setIsAnalyzing(false);
            await refreshData();
        }
    };
    
    const filteredMentions = state.mentions.filter(m => filter === 'All' || m.sentiment === filter);
    const progressValue = progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

    return (
        <Container maxWidth="lg">
            <Typography variant="h4" gutterBottom>Sentiment Analysis</Typography>
            <Card sx={{ mb: 3 }}><CardContent>
                <Typography variant="h6" gutterBottom>Actions</Typography>
                <Button fullWidth variant="contained" color="success" onClick={handleAnalyze} disabled={isAnalyzing}>
                    {isAnalyzing 
                        ? `Analyzing... (${progress.current} / ${progress.total})` 
                        : `Analyze ${state.mentions.filter(m => m.id && (!m.sentiment || m.sentiment === 'Unanalyzed')).length} Unanalyzed Mentions`}
                </Button>
                {isAnalyzing && <LinearProgress variant="determinate" value={progressValue} sx={{ mt: 2 }} />}
            </CardContent></Card>
            <Card><CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Mentions by Sentiment</Typography>
                    <Stack direction="row" spacing={1}>
                        {['All', 'Positive', 'Negative', 'Neutral', 'Unanalyzed'].map(f => (
                            <Chip key={f} label={f} onClick={() => setFilter(f)} variant={filter === f ? 'filled' : 'outlined'} />
                        ))}
                    </Stack>
                </Box>
                <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                    {filteredMentions.slice(0, 200).map(m => <MentionCard key={m.id || m.postUrl} mention={m} onGenerateReply={onGenerateReply} />)}
                </Box>
            </CardContent></Card>
        </Container>
    );
};

export default SentimentPage;
